// @ts-nocheck
// Ported byte-identically from obd3-gui v1
// (src/features/dashboard/components/AddWidgetSheet.tsx).
// Right-side slide-over widget library. Lists widgets eligible for the
// active tab + current user role; tiles already on the canvas render as
// disabled "On canvas" chips.
import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils/cn';
import { WIDGETS, isWidgetAllowed, type TabId, type WidgetId } from '../config';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tab: TabId;
  role: string;
  existingIds: WidgetId[];
  onAdd: (id: WidgetId) => void;
}

export function AddWidgetSheet({
  open,
  onOpenChange,
  tab,
  role,
  existingIds,
  onAdd,
}: Props) {
  const placed = useMemo(() => new Set(existingIds), [existingIds]);
  const candidates = useMemo(
    () => WIDGETS.filter((w) => w.tab === tab && w.roles.includes(role) && isWidgetAllowed(w.id)),
    [tab, role],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Widget library</SheetTitle>
          <SheetDescription>
            Add tiles to this tab. Items already on the canvas are dimmed.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 grid grid-cols-1 gap-2">
          {candidates.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">
              No widgets available for your role on this tab.
            </p>
          ) : (
            candidates.map((w) => {
              const Icon = w.icon;
              const already = placed.has(w.id);
              return (
                <button
                  key={w.id}
                  type="button"
                  disabled={already}
                  onClick={() => onAdd(w.id)}
                  className={cn(
                    'group flex w-full items-start gap-2.5 rounded-lg border bg-card p-3 text-left shadow-sm transition-all',
                    already
                      ? 'cursor-not-allowed border-border opacity-50'
                      : 'border-border hover:-translate-y-px hover:border-primary/40 hover:shadow-md',
                  )}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-foreground">
                        {w.title}
                      </span>
                      {already && (
                        <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                          On canvas
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{w.description}</p>
                  </div>
                  {!already && (
                    <Plus className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
